import type { CountdownBlock } from "@aswin.dev/types";
import { useIntervalFn } from "@vueuse/core";
import { computed, ref, type ComputedRef, type Ref } from "vue";

export interface CountdownRemaining {
  readonly days: number;
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;
}

export interface CountdownSegment {
  readonly key: "days" | "hours" | "minutes" | "seconds";
  readonly value: string;
  readonly raw: number;
  readonly label: string;
}

export interface CountdownTimer {
  readonly now: Ref<number>;
  readonly targetTime: ComputedRef<number | null>;
  readonly isExpired: ComputedRef<boolean>;
  readonly remaining: ComputedRef<CountdownRemaining>;
  readonly segments: ComputedRef<CountdownSegment[]>;
  readonly hasTargetDate: ComputedRef<boolean>;
}

const ZERO_REMAINING: CountdownRemaining = {
  days: 0,
  hours: 0,
  minutes: 0,
  seconds: 0,
};

/**
 * Centralizes the countdown timer + segment derivation that every themed
 * variant needs. Components can render whatever DOM/animation they want
 * around the same shared, ticking source of truth.
 */
export function useCountdownTimer(
  block: ComputedRef<CountdownBlock> | { value: CountdownBlock },
): CountdownTimer {
  const now = ref(Date.now());
  useIntervalFn(() => {
    now.value = Date.now();
  }, 1000);

  const targetTime = computed(() => {
    const target = block.value.targetDate;
    if (!target) return null;
    const date = new Date(target);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  });

  const hasTargetDate = computed(() => Boolean(block.value.targetDate));

  const isExpired = computed(() => {
    if (!targetTime.value) return false;
    return now.value >= targetTime.value;
  });

  const remaining = computed<CountdownRemaining>(() => {
    if (!targetTime.value || isExpired.value) return ZERO_REMAINING;
    const diff = targetTime.value - now.value;
    const totalSeconds = Math.floor(diff / 1000);
    return {
      days: Math.floor(totalSeconds / 86400),
      hours: Math.floor((totalSeconds % 86400) / 3600),
      minutes: Math.floor((totalSeconds % 3600) / 60),
      seconds: totalSeconds % 60,
    };
  });

  const segments = computed<CountdownSegment[]>(() => {
    const b = block.value;
    const r = remaining.value;
    const result: CountdownSegment[] = [];
    if (b.showDays) {
      result.push({
        key: "days",
        raw: r.days,
        value: String(r.days).padStart(2, "0"),
        label: b.labelDays,
      });
    }
    if (b.showHours) {
      result.push({
        key: "hours",
        raw: r.hours,
        value: String(r.hours).padStart(2, "0"),
        label: b.labelHours,
      });
    }
    if (b.showMinutes) {
      result.push({
        key: "minutes",
        raw: r.minutes,
        value: String(r.minutes).padStart(2, "0"),
        label: b.labelMinutes,
      });
    }
    if (b.showSeconds) {
      result.push({
        key: "seconds",
        raw: r.seconds,
        value: String(r.seconds).padStart(2, "0"),
        label: b.labelSeconds,
      });
    }
    return result;
  });

  return {
    now,
    targetTime,
    isExpired,
    remaining,
    segments,
    hasTargetDate,
  };
}

/**
 * Maps the `animationSpeed` knob to a CSS duration multiplier so themed
 * components can scale their animations consistently.
 */
export function animationDurationMs(
  speed: CountdownBlock["animationSpeed"],
  base: number,
): number {
  switch (speed) {
    case "slow":
      return Math.round(base * 1.6);
    case "fast":
      return Math.round(base * 0.65);
    default:
      return base;
  }
}
